import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Rocket, 
  Zap, 
  Eye, 
  Code, 
  Shield, 
  GitBranch,
  Layers,
  RefreshCw,
  Check,
  Bot,
  Lock
} from 'lucide-react';

export const DeployPage = () => {
  const heroFeatures = [
    { icon: Zap, label: 'Sub-Second Loads' },
    { icon: Eye, label: 'Branch Previews' },
    { icon: Code, label: 'EdgeJS' },
    { icon: Shield, label: 'Dual WAAP Security' },
  ];

  const securityFeatures = [
    {
      title: 'Audit Mode',
      description: 'Test new rules safely without blocking real users.'
    },
    {
      title: 'Bot Management',
      description: 'ML-powered detection for scrapers and credential stuffing.'
    },
    {
      title: 'Client-Side Protection',
      description: 'Stop malicious scripts in your users\' browsers.'
    }
  ];

  const platformFeatures = [
    {
      icon: Layers,
      title: '40+ Frameworks',
      description: 'Native support for Next.js, Nuxt, Angular, Vue, Svelte, and more. We adapt to your stack, not the other way around.'
    },
    {
      icon: Code,
      title: 'Edge Functions',
      description: 'Run serverless compute at the edge. Handle authentication, personalization, and API orchestration closer to the user.'
    },
    {
      icon: RefreshCw,
      title: 'Atomic Deploys',
      description: 'Instant rollbacks and immutable deployments. Ship with the confidence that you can undo anything in seconds.'
    }
  ];

  const edgeJSFeatures = [
    {
      title: 'Local Emulation',
      description: 'Run your edge logic locally. Debug headers, cache keys, and routing rules on \'localhost\'.'
    },
    {
      title: 'Branch Previews',
      description: 'Every pull request gets a live URL with the exact edge configuration of production.'
    },
    {
      title: 'Integrated Security',
      description: 'Apply WAF and bot protection rules alongside your routing logic.'
    }
  ];

  return (
    <div className="bg-[#0a0f1a] min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 px-4 md:px-8 lg:px-12 text-center">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#F97316]/10 border border-[#F97316]/30 px-4 py-2 mb-8 text-sm text-[#F97316] rounded-full">
            <Rocket size={16} />
            Parler Deploy
            <span className="bg-[#F97316] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ml-1">
              Coming Soon
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
            Build. Release. Protect.
            <br />
            <span className="text-[#F97316]">Accelerate.</span>
          </h1>

          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-10">
            The unified deployment platform for the fastest sites on earth. Combine 
            the agility of Jamstack with the power of an enterprise edge.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link 
              to="/packages?tab=deploy" 
              className="inline-flex items-center justify-center gap-2 bg-[#F97316] text-white px-8 py-3.5 font-semibold hover:bg-[#EA580C] transition-all rounded-xl hover:-translate-y-1"
            >
              Get Started
            </Link>
            <Link 
              to="/packages?tab=deploy" 
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/20 text-white px-8 py-3.5 font-semibold hover:bg-white/5 transition-all rounded-xl hover:-translate-y-1"
            >
              See Deploy Pricing
            </Link>
          </div>

          {/* Hero Features Bar */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 text-white/50 text-sm">
            {heroFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="flex items-center gap-2">
                  <Icon size={16} className="text-[#F97316]" />
                  <span className="font-mono">{feature.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrated Security Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                Integrated Security from Commit to Edge
              </h2>
              <p className="text-white/60 mb-8">
                Don't bolt security on later. Build with protection. Our Dual WAAP lets you test rules in 
                audit mode against production traffic so you never break legitimate users.
              </p>

              <div className="space-y-4">
                {securityFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full border border-[#F97316]/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-[#F97316]"></div>
                    </div>
                    <div>
                      <span className="text-white font-semibold">{feature.title}:</span>
                      <span className="text-white/60 ml-1">{feature.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0d1526] border border-white/10 p-8 md:p-12 text-center rounded-2xl">
              <div className="text-6xl md:text-7xl font-bold text-[#F97316] mb-2">
                1<span className="text-3xl md:text-4xl">ms</span>
              </div>
              <div className="text-white/60 uppercase tracking-wider text-sm mb-1">Security Execution</div>
              <div className="text-white/40 text-sm">Zero Performance Penalty</div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[#0d1526]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {platformFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="bg-[#0a0f1a] border border-white/10 p-6 md:p-8 hover:border-[#F97316]/30 transition-all rounded-2xl hover:-translate-y-1">
                  <Icon size={28} className="text-[#F97316] mb-4" />
                  <h3 className="text-lg md:text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EdgeJS Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Code Block */}
            <div className="order-2 lg:order-1">
              <div className="bg-[#1a1f2e] border border-white/10 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#0d1526]">
                  <span className="w-3 h-3 rounded-full bg-[#EF4444]"></span>
                  <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-white/50 text-sm ml-2 font-mono">routes.js</span>
                </div>
                <pre className="p-4 text-sm overflow-x-auto font-mono">
                  <code>
                    <span className="text-[#7C3AED]">import</span>{" "}
                    <span className="text-white">{"{ "}</span>
                    <span className="text-[#10B981]">Router</span>
                    <span className="text-white">{" }"}</span>{" "}
                    <span className="text-[#7C3AED]">from</span>{" "}
                    <span className="text-[#F97316]">'@parler/edge'</span>
                    <span className="text-white">;</span>
                    {"\n\n"}
                    <span className="text-[#7C3AED]">export default new</span>{" "}
                    <span className="text-[#10B981]">Router</span>
                    <span className="text-white">()</span>
                    {"\n"}
                    <span className="text-white">  .</span>
                    <span className="text-[#3B82F6]">get</span>
                    <span className="text-white">(</span>
                    <span className="text-[#F97316]">'/p/:id'</span>
                    <span className="text-white">, ({"{ "}cache, proxy{" }"}) =&gt; {"{"}</span>
                    {"\n"}
                    <span className="text-white">    </span>
                    <span className="text-[#3B82F6]">cache</span>
                    <span className="text-white">({"{"}</span>
                    {"\n"}
                    <span className="text-white">      edge: {"{ "}</span>
                    <span className="text-[#10B981]">maxAgeSeconds</span>
                    <span className="text-white">: </span>
                    <span className="text-[#F97316]">60 * 60 * 24</span>
                    <span className="text-white">{" }"},</span>
                    {"\n"}
                    <span className="text-white">      </span>
                    <span className="text-[#10B981]">staleWhileRevalidateSeconds</span>
                    <span className="text-white">: </span>
                    <span className="text-[#F97316]">60</span>
                    {"\n"}
                    <span className="text-white">    {"}"});</span>
                    {"\n"}
                    <span className="text-white">    </span>
                    <span className="text-[#3B82F6]">proxy</span>
                    <span className="text-white">(</span>
                    <span className="text-[#F97316]">'origin'</span>
                    <span className="text-white">);</span>
                    {"\n"}
                    <span className="text-white">  {"}"})</span>
                    {"\n"}
                    <span className="text-white">  .</span>
                    <span className="text-[#3B82F6]">use</span>
                    <span className="text-white">(</span>
                    <span className="text-[#10B981]">nextRoutes</span>
                    <span className="text-white">);</span>
                    <span className="text-white/40"> // Native Next.js support</span>
                  </code>
                </pre>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-[#F97316]/10 border border-[#F97316]/30 px-3 py-1.5 mb-6 text-xs text-[#F97316] rounded-full">
                <Code size={14} />
                EdgeJS
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                CDN-as-Code.
              </h2>

              <p className="text-base md:text-lg text-white/60 mb-8">
                Stop clicking through portals. Define your edge logic, caching rules, and routing 
                directly in JavaScript. Version control your infrastructure.
              </p>

              <div className="space-y-4">
                {edgeJSFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check size={20} className="text-[#F97316] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-white font-semibold">{feature.title}:</span>
                      <span className="text-white/60 ml-1">{feature.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[#0d1526]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            Start shipping today
          </h2>
          <p className="text-base md:text-lg text-white/60 mb-8">
            Connect your repo and deploy in under 2 minutes. No credit card required to start.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/packages?tab=deploy" 
              className="inline-flex items-center justify-center gap-2 bg-[#F97316] text-white px-8 py-3.5 font-semibold hover:bg-[#EA580C] transition-all rounded-xl hover:-translate-y-1"
            >
              Get Started Free
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/cloud" 
              className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white px-8 py-3.5 font-semibold hover:bg-white/5 transition-all rounded-xl hover:-translate-y-1"
            >
              Pair with Parler Cloud
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeployPage;
